import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ListingCard from 'src/components/elements/listing-card/ListingCard'
import InteractionButtons from './interaction-buttons/InteractionButtons'
import { IProperty } from 'src/interfaces/IProperty'
import { useMakeFavoriteMutation } from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { pulseFavorite } from 'src/redux/slices/uiSlice'

export interface IPropertyCard {
  property: IProperty
  propertySearchId?: string
  onClick?: () => void
  className?: string
}

const chip = 'px-2 py-0.5 rounded-lg text-xs font-extrabold'

// A listing matched to a saved request (cabinet inbox, favourites): the one
// listing card, opened in a modal when the parent passes onClick, with the
// request's own state (heart per request, owner responded, updated, price
// dropped) as chips under it.
const PropertyCard: FC<IPropertyCard> = ({ property, propertySearchId, onClick, className }) => {
  const { t } = useTranslation()
  const [makeFavorite] = useMakeFavoriteMutation()
  const dispatch = useAppDispatch()
  const [isFavorites, setIsFavorites] = useState(property.isFavorite)

  // The listing's own currency, like every other card; the first converted
  // price when the payload does not say which one is original.
  const price =
    property.price.find((option) => option.currency === property.currency) || property.price[0]
  const isPromoted = !!property.promotedUntil && new Date(property.promotedUntil) > new Date()
  const { street, settlement, district } = property.addressSection
  const location = [street, settlement, district].filter(Boolean).join(', ')

  const handleMakeFavorite = (value: boolean) => {
    const actualPropertySearchId = property.propertySearchId || propertySearchId
    if (!actualPropertySearchId) {
      console.error('No property search ID available')
      return
    }
    makeFavorite({
      propertyId: property.id,
      propertySearchId: actualPropertySearchId,
      isFavorite: value,
    })
    setIsFavorites(value)
    // Pulse the sidebar "Вибране" item on a save, like FavoriteHeartButton does
    // on the map/announcement surfaces (owner feedback).
    if (value) dispatch(pulseFavorite())
  }

  const hasChips =
    property.priceDropped ||
    property.parametersSection?.bargaining ||
    property.hasResponded ||
    property.ownerUpdatedAt

  return (
    <ListingCard
      id={property.id}
      href={onClick ? undefined : `/announcement/${property.id}`}
      onOpen={onClick}
      images={property.images || []}
      alt={location}
      price={price}
      listingType={property.listingSection?.listingType}
      propertyType={property.listingSection?.propertyType}
      parameters={property.parametersSection}
      location={location}
      className={className}
      topLeft={
        isPromoted && (
          <span className="px-2 py-1 text-xs font-black text-white bg-brand-strong rounded-lg shadow">
            {t('promote.badge_top')}
          </span>
        )
      }
      actions={
        <InteractionButtons
          setIsFavorites={(value) => handleMakeFavorite(value)}
          isFavorites={isFavorites}
          propertyId={property.id}
          ownerId={property.ownerId || property.user?.id}
        />
      }
      badges={
        hasChips && (
          <>
            {property.hasResponded && (
              <span className={`${chip} bg-brand-soft text-brand-ink`}>
                {t('property.owner_responded')}
              </span>
            )}
            {property.priceDropped && (
              <span className={`${chip} bg-success-soft text-success`}>
                {t('property.price_dropped')}
              </span>
            )}
            {property.ownerUpdatedAt && (
              <span className={`${chip} bg-warning-soft text-warning`}>
                {t('property.updated_badge')}
              </span>
            )}
            {property.parametersSection?.bargaining && (
              <span className={`${chip} bg-muted-soft text-muted`}>
                {t('property.negotiation')}
              </span>
            )}
          </>
        )
      }
    />
  )
}

export default PropertyCard
